/**
 * PhotoLightbox — fullscreen viewer для изображений в чате.
 *
 * Поддерживает:
 *  - Mouse wheel zoom (зум вокруг курсора)
 *  - Pinch zoom (touch / iOS gestures)
 *  - Double-click toggle zoom (1 ↔ 2.5x)
 *  - Pan мышью когда зумлено
 *  - Стрелки клавиатуры + arrow buttons для prev/next
 *  - Esc для close
 *  - Swipe-навигация на touch когда НЕ зумлено
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/prototype/Icon";
import { downloadChatAttachment } from "@/lib/downloadAttachment";

export interface LightboxImage {
  src: string;
  previewSrc?: string;
  filename?: string;
  caption?: string;
  attachmentId?: number;
  onJump?: () => void;
  /** Подпись для onJump кнопки (по умолчанию «Перейти к сообщению»). */
  jumpLabel?: string;
  onViewAll?: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.25;          // wheel-tick zoom factor
const DOUBLE_CLICK_SCALE = 2.5;
const SWIPE_PX = 60;

// Кнопки верхней панели: pill — с подписью (ПК), icon — круглая без подписи (телефон).
const lbPillBtnStyle: CSSProperties = {
  flex: "0 0 auto", height: 44, borderRadius: 22,
  background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
  cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  gap: 6, padding: "0 16px", fontSize: 14, fontFamily: "inherit",
  whiteSpace: "nowrap",
};
const lbIconBtnStyle: CSSProperties = {
  ...lbPillBtnStyle, width: 44, padding: 0, borderRadius: "50%",
};
// Телефон: кнопки внутри сплошной верхней полосы — без собственного фона
// (полупрозрачные круги просвечивали содержимое снимка и слипались со счётчиком).
const lbBarBtnStyle: CSSProperties = {
  ...lbIconBtnStyle, background: "transparent", borderRadius: 10,
};

export function PhotoLightbox({
  images, idx, setIdx, onClose, onSaveToDrive,
}: {
  images: LightboxImage[];
  idx: number;
  setIdx: (i: number) => void;
  onClose: () => void;
  /** Если задан и текущая картинка имеет attachmentId — показывается кнопка
   *  «Добавить на диск». Callback получает { attachmentId, filename }. */
  onSaveToDrive?: (img: { attachmentId: number; filename?: string }) => void;
}) {
  // Узкий экран (телефон): кнопки верхней панели без подписей, только иконки.
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Refs — для event handlers (избегаем re-mount listeners на каждый scale change)
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  scaleRef.current = scale;
  txRef.current = tx;
  tyRef.current = ty;

  // Touch state
  const pinchRef = useRef<{ d0: number; s0: number; cx0: number; cy0: number } | null>(null);
  const panRef = useRef<{ x0: number; y0: number; tx0: number; ty0: number } | null>(null);
  const swipeRef = useRef<{ x: number; y: number } | null>(null);

  // Mouse drag state
  const mouseDragRef = useRef<{ x0: number; y0: number; tx0: number; ty0: number } | null>(null);

  // Reset zoom при смене картинки
  useEffect(() => {
    setScale(1); setTx(0); setTy(0); setImgLoaded(false);
  }, [idx]);

  const next = useCallback(() => {
    if (idx < images.length - 1) setIdx(idx + 1);
  }, [idx, images.length, setIdx]);

  const prev = useCallback(() => {
    if (idx > 0) setIdx(idx - 1);
  }, [idx, setIdx]);

  // Keyboard: arrows + Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); return; }
      if (e.key === "ArrowRight") { e.preventDefault(); next(); return; }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [prev, next, onClose]);

  // Body scroll lock + iOS PWA viewport lock (anti page-zoom)
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const meta = document.querySelector('meta[name="viewport"]');
    const origContent = meta?.getAttribute("content") || "";
    if (meta) {
      meta.setAttribute(
        "content",
        "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
      );
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      if (meta && origContent) meta.setAttribute("content", origContent);
    };
  }, []);

  // === ZOOM CORE: cursor-anchored ===
  // Меняет scale, корректирует tx/ty чтобы точка под cursor оставалась на месте.
  // cx, cy — координаты cursor относительно центра контейнера (px).
  const applyZoom = useCallback((newScale: number, cx: number, cy: number) => {
    const cur = scaleRef.current;
    const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    if (next === cur) return;
    if (next <= MIN_SCALE) {
      setScale(MIN_SCALE); setTx(0); setTy(0);
      return;
    }
    // transform-origin: center (CSS default). Формула: point in image
    // coordinates: (cx - tx) / cur. After zoom: cursor должен остаться на (cx, cy).
    // new_tx = cx - (cx - cur_tx) * (next / cur)
    const ratio = next / cur;
    const newTx = cx - (cx - txRef.current) * ratio;
    const newTy = cy - (cy - tyRef.current) * ratio;
    setScale(next);
    setTx(newTx);
    setTy(newTy);
  }, []);

  // === WHEEL ZOOM === — attach on document level (PWA portal-based lightbox
  // может быть вне React tree, addEventListener на containerRef иногда не fire'ит
  // если события перехвачены раньше). Document level гарантирует получение.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      // Check что курсор над lightbox container (не над другим element)
      const el = containerRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - (rect.left + rect.width / 2);
      const cy = e.clientY - (rect.top + rect.height / 2);
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      applyZoom(scaleRef.current * factor, cx, cy);
    };
    document.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => document.removeEventListener("wheel", onWheel, { capture: true } as EventListenerOptions);
  }, [applyZoom]);

  // === iOS GESTURE (Safari только) — preventDefault ===
  useEffect(() => {
    const onGesture = (e: Event) => e.preventDefault();
    document.addEventListener("gesturestart", onGesture, { passive: false } as AddEventListenerOptions);
    document.addEventListener("gesturechange", onGesture, { passive: false } as AddEventListenerOptions);
    document.addEventListener("gestureend", onGesture, { passive: false } as AddEventListenerOptions);
    return () => {
      document.removeEventListener("gesturestart", onGesture);
      document.removeEventListener("gesturechange", onGesture);
      document.removeEventListener("gestureend", onGesture);
    };
  }, []);

  // === TOUCH (pinch + pan + swipe) ===
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const dist = (t1: Touch, t2: Touch) =>
      Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        pinchRef.current = {
          d0: dist(e.touches[0], e.touches[1]),
          s0: scaleRef.current,
          cx0: cx - (rect.left + rect.width / 2),
          cy0: cy - (rect.top + rect.height / 2),
        };
        swipeRef.current = null;
        panRef.current = null;
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        if (scaleRef.current > 1) {
          panRef.current = { x0: t.clientX, y0: t.clientY, tx0: txRef.current, ty0: tyRef.current };
        } else {
          swipeRef.current = { x: t.clientX, y: t.clientY };
        }
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const d = dist(e.touches[0], e.touches[1]);
        const newScale = pinchRef.current.s0 * (d / pinchRef.current.d0);
        applyZoom(newScale, pinchRef.current.cx0, pinchRef.current.cy0);
        return;
      }
      if (e.touches.length === 1 && panRef.current) {
        e.preventDefault();
        const t = e.touches[0];
        setTx(panRef.current.tx0 + (t.clientX - panRef.current.x0));
        setTy(panRef.current.ty0 + (t.clientY - panRef.current.y0));
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (pinchRef.current && e.touches.length < 2) {
        pinchRef.current = null;
        if (scaleRef.current < 1.05) { setScale(1); setTx(0); setTy(0); }
        return;
      }
      if (panRef.current) { panRef.current = null; return; }
      if (swipeRef.current && e.changedTouches[0]) {
        const start = swipeRef.current;
        swipeRef.current = null;
        const t = e.changedTouches[0];
        const dx = t.clientX - start.x;
        const dy = t.clientY - start.y;
        if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
          if (dx > 0) prev(); else next();
        } else if (dy > SWIPE_PX * 2 && Math.abs(dy) > Math.abs(dx)) {
          // Свайп ВНИЗ → закрыть (как в TG). Раньше условие было dy < -X —
          // закрывало по свайпу ВВЕРХ, вопреки комментарию (инверсия жеста).
          onClose();
        }
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [applyZoom, next, prev, onClose]);

  // === MOUSE PAN (когда zoomed) ===
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onDown = (e: MouseEvent) => {
      if (scaleRef.current <= 1) return;
      if (e.button !== 0) return;
      e.preventDefault();
      mouseDragRef.current = { x0: e.clientX, y0: e.clientY, tx0: txRef.current, ty0: tyRef.current };
    };
    const onMove = (e: MouseEvent) => {
      if (!mouseDragRef.current) return;
      e.preventDefault();
      setTx(mouseDragRef.current.tx0 + (e.clientX - mouseDragRef.current.x0));
      setTy(mouseDragRef.current.ty0 + (e.clientY - mouseDragRef.current.y0));
    };
    const onUp = () => { mouseDragRef.current = null; };
    el.addEventListener("mousedown", onDown);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      el.removeEventListener("mousedown", onDown);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // === DOUBLE-CLICK toggle zoom ===
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = e.clientX - (rect.left + rect.width / 2);
    const cy = e.clientY - (rect.top + rect.height / 2);
    if (scaleRef.current > 1.1) {
      setScale(1); setTx(0); setTy(0);
    } else {
      applyZoom(DOUBLE_CLICK_SCALE, cx, cy);
    }
  }, [applyZoom]);

  if (idx < 0 || idx >= images.length) return null;
  const img = images[idx];
  const displaySrc = imgLoaded ? img.src : (img.previewSrc || img.src);

  return createPortal(
    <div
      ref={containerRef}
      className="media-lightbox"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.92)",
        zIndex: 10000,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        // Плавное появление вместо hard-cut. Лайтбокс портирован в body (вне
        // .chat-redesign), поэтому глобальный animation:none на него не влияет —
        // инлайн-animation работает без !important. keyframe lb-fadein из chat.css.
        animation: "lb-fadein 0.2s ease-out",
      }}
      onClick={(e) => {
        // Click на backdrop (вне img) → close
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Верхняя панель одной flex-строкой: Скачать / Добавить на диск / Закрыть.
          На узком экране кнопки без подписей (как в VideoLightbox). */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={narrow ? {
          // Телефон: сплошная тёмная полоса во всю ширину под safe-area (как в
          // TG): счётчик слева, кнопки справа. Раньше круглые полупрозрачные
          // кнопки и центрированный счётчик наезжали друг на друга и на
          // статус-бар самого снимка (16.09.2026).
          position: "absolute", top: 0, left: 0, right: 0,
          boxSizing: "border-box",
          paddingTop: "env(safe-area-inset-top, 0px)",
          height: "calc(56px + env(safe-area-inset-top, 0px))",
          paddingLeft: 16, paddingRight: 4,
          background: "rgba(0,0,0,0.62)",
          display: "flex", alignItems: "center", gap: 2,
          zIndex: 10001,
        } : {
          position: "absolute", top: 16, right: 16,
          display: "flex", alignItems: "center", gap: 8,
          zIndex: 10001,
        }}
      >
        {narrow && (
          <span style={{ flex: 1, minWidth: 0, color: "#fff", fontSize: 15, fontWeight: 500, whiteSpace: "nowrap" }}>
            {images.length > 1 ? `${idx + 1} / ${images.length}` : ""}
          </span>
        )}
        {/* Скачать: ПК — <a download> с ?download=1 (Content-Disposition:
            attachment), телефон — Web Share («Сохранить изображение» на iOS). */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const cur = images[idx];
            if (!cur) return;
            void downloadChatAttachment({ attachmentId: cur.attachmentId, url: cur.src, filename: cur.filename });
          }}
          title="Скачать фото"
          style={narrow ? lbBarBtnStyle : lbPillBtnStyle}
        >
          <Icon name="download" size={18}/>
          {!narrow && "Скачать"}
        </button>
        {/* Save-to-drive (если есть attachmentId + callback) */}
        {onSaveToDrive && images[idx]?.attachmentId != null && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const cur = images[idx];
              if (cur.attachmentId != null) onSaveToDrive({ attachmentId: cur.attachmentId, filename: cur.filename });
            }}
            title="Добавить на диск"
            style={narrow ? lbBarBtnStyle : lbPillBtnStyle}
          >
            <Icon name="folder" size={18}/>
            {!narrow && "Добавить на диск"}
          </button>
        )}
        {/* Close */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          title="Закрыть (Esc)"
          style={narrow ? lbBarBtnStyle : lbIconBtnStyle}
        >
          <Icon name="x" size={24}/>
        </button>
      </div>

      {/* Prev arrow */}
      {idx > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          title="Назад"
          style={{
            position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
            width: 48, height: 48, borderRadius: "50%",
            background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
            cursor: "pointer", zIndex: 10001,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name="chevron_l" size={28}/>
        </button>
      )}

      {/* Next arrow */}
      {idx < images.length - 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); next(); }}
          title="Вперёд"
          style={{
            position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)",
            width: 48, height: 48, borderRadius: "50%",
            background: "rgba(0,0,0,0.5)", border: "none", color: "#fff",
            cursor: "pointer", zIndex: 10001,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <Icon name="chevron_r" size={28}/>
        </button>
      )}

      {/* Image */}
      <img
        ref={imgRef}
        key={idx}
        src={displaySrc}
        alt={img.filename || ""}
        decoding="async"
        fetchPriority="high"
        onLoad={() => setImgLoaded(true)}
        onDoubleClick={handleDoubleClick}
        draggable={false}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transition: "none",
          // Фейд при смене фото (img remount по key={idx}) — раньше был мёртв,
          // т.к. класс media-lightbox-img не применялся. lb-fadein анимирует
          // только opacity, с inline transform (zoom/pan) не конфликтует.
          animation: "lb-fadein 0.18s ease-out",
          cursor: scale > 1 ? "grab" : "zoom-in",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitUserDrag: "none",
          touchAction: "none",
        } as React.CSSProperties}
      />

      {/* Counter (ПК — по центру сверху; на телефоне он в верхней полосе) */}
      {!narrow && images.length > 1 && (
        <div style={{
          position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
          color: "#fff", background: "rgba(0,0,0,0.5)",
          padding: "6px 14px", borderRadius: 16,
          fontSize: 13, fontWeight: 500,
          zIndex: 10001,
          pointerEvents: "none",
        }}>
          {idx + 1} / {images.length}
        </div>
      )}

      {/* Caption + Jump button (under image) */}
      {(img.caption || img.onJump) && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute", bottom: 16, left: 16, right: 16,
            display: "flex", justifyContent: "center", gap: 12,
            zIndex: 10001,
            pointerEvents: "none",
          }}
        >
          {img.caption && (
            <div style={{
              background: "rgba(0,0,0,0.5)", color: "#fff",
              padding: "6px 14px", borderRadius: 14, fontSize: 13,
              maxWidth: "70%",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              pointerEvents: "auto",
            }}>
              {img.caption}
            </div>
          )}
          {img.onJump && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); img.onJump!(); onClose(); }}
              style={{
                background: "rgba(0,0,0,0.5)", color: "#fff",
                border: "none", padding: "6px 14px", borderRadius: 14,
                fontSize: 13, cursor: "pointer",
                pointerEvents: "auto",
              }}
            >
              {img.jumpLabel ?? "Перейти к сообщению"}
            </button>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}
